import { Injectable } from '@angular/core';
import { Actions, createEffect, EffectsModule, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { VehicleService } from '@polaris-customerweb/sharedcomponents.garage';
import { LogService } from '@polaris-customerweb/sharedcomponents.shared';
import { forkJoin, of } from 'rxjs';
import {
    catchError,
    defaultIfEmpty,
    exhaustMap,
    filter,
    map,
    switchMap,
    withLatestFrom,
} from 'rxjs/operators';
import { AppState } from 'src/app/models/app-state';
import * as GarageActions from '../garage/garage.actions';
import { VehicleCategory } from './models/industry-dropdowns.model';
import { IndustryDropdownsService } from './services/industry-dropdowns.service';
import * as VehicleActions from './vehicle.actions';

@Injectable()
export class VehicleEffects {
    readonly className = 'VehicleEffects';

    getVinInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VehicleActions.GetVinInfo),
            map(({ vehicles }) => {
                return vehicles.map((vehicle) =>
                    this.vehicleService.attachVinInfo$(vehicle)
                );
            }),
            switchMap((vehicles$) => {
                if (!vehicles$ || vehicles$.length === 0) {
                    return of([]);
                }

                return forkJoin(vehicles$).pipe(defaultIfEmpty([]));
            }),
            switchMap((vehicles) => [
                VehicleActions.GetUserVehiclesSuccess({ vehicles }),
                GarageActions.GetGarageItemsSuccess(),
            ])
        )
    );

    getVehicleCategories$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VehicleActions.GetVehicleCategories),
            withLatestFrom(this.store$),
            filter(([, state]) => !state.vehicleState.vehicleCategories.data),
            exhaustMap(() =>
                this.industryDropdownsService.getVehicleCategories().pipe(
                    map((payload) =>
                        VehicleActions.GetVehicleCategoriesSuccess({
                            vehicleCategories: payload,
                        })
                    ),
                    catchError((errorMessage) => {
                        this.logService.error(
                            this.className,
                            errorMessage?.message
                        );
                        return of(
                            VehicleActions.GetVehicleCategoriesFail({
                                errorMessage,
                            })
                        );
                    })
                )
            )
        )
    );

    getVehicleCategoriesAlreadyLoaded$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VehicleActions.GetVehicleCategories),
            withLatestFrom(this.store$),
            filter(([, state]) =>
                Boolean(state.vehicleState.vehicleCategories.data)
            ),
            switchMap(([, state]) => [
                VehicleActions.GetVehicleCategoriesSuccess({
                    vehicleCategories: state.vehicleState.vehicleCategories
                        .data as VehicleCategory,
                }),
            ])
        )
    );

    categorySelected$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VehicleActions.AddVehicleSelectCategory),
            withLatestFrom(this.store$),
            filter(
                ([{ categoryId }, state]) =>
                    !state.vehicleState.vehicleMakes.entities[categoryId]
            ),
            switchMap(([{ categoryId }]) => [
                VehicleActions.GetVehicleMakes({ categoryId }),
            ])
        )
    );

    getVehicleMakes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VehicleActions.GetVehicleMakes),
            withLatestFrom(this.store$),
            switchMap(([{ categoryId }]) =>
                this.industryDropdownsService.getVehicleMakes(categoryId).pipe(
                    map((payload) => {
                        payload.id = categoryId;
                        return VehicleActions.GetVehicleMakesSuccess({
                            vehicleMakes: payload,
                        });
                    }),
                    catchError((errorMessage) => {
                        this.logService.error(
                            this.className,
                            errorMessage?.message
                        );
                        return of(
                            VehicleActions.GetVehicleMakesFail({
                                errorMessage,
                            })
                        );
                    })
                )
            )
        )
    );

    makeSelected$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VehicleActions.AddVehicleSelectMake),
            withLatestFrom(this.store$),
            filter(
                ([{ makeId }, state]) =>
                    !state.vehicleState.vehicleYears.entities[makeId]
            ),
            switchMap(([{ categoryId, makeId }]) => [
                VehicleActions.GetVehicleYears({ categoryId, makeId }),
            ])
        )
    );

    getVehicleYears$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VehicleActions.GetVehicleYears),
            withLatestFrom(this.store$),
            switchMap(([{ categoryId, makeId }]) =>
                this.industryDropdownsService
                    .getVehicleYears(categoryId, makeId)
                    .pipe(
                        map((payload) => {
                            return VehicleActions.GetVehicleYearsSuccess({
                                vehicleYears: { id: makeId, years: payload },
                            });
                        }),
                        catchError((errorMessage) => {
                            this.logService.error(
                                this.className,
                                errorMessage?.message
                            );
                            return of(
                                VehicleActions.GetVehicleYearsFail({
                                    errorMessage,
                                })
                            );
                        })
                    )
            )
        )
    );

    yearSelected$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VehicleActions.AddVehicleSelectYear),
            withLatestFrom(this.store$),
            filter(
                ([{ makeId, year }, state]) =>
                    !state.vehicleState.vehicleYears.entities[
                        `${makeId}${year}`
                    ]
            ),
            switchMap(([{ categoryId, makeId, year }]) => [
                VehicleActions.GetVehicleModels({ categoryId, makeId, year }),
            ])
        )
    );

    getVehicleModels$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VehicleActions.GetVehicleModels),
            withLatestFrom(this.store$),
            switchMap(([{ categoryId, makeId, year }]) =>
                this.industryDropdownsService
                    .getVehicleModels(categoryId, makeId, year)
                    .pipe(
                        map((payload) => {
                            payload.id = `${makeId}${year}`;
                            return VehicleActions.GetVehicleModelsSuccess({
                                vehicleModels: payload,
                            });
                        }),
                        catchError((errorMessage) => {
                            this.logService.error(
                                this.className,
                                errorMessage?.message
                            );
                            return of(
                                VehicleActions.GetVehicleModelsFail({
                                    errorMessage,
                                })
                            );
                        })
                    )
            )
        )
    );

    constructor(
        private readonly actions$: Actions,
        private readonly industryDropdownsService: IndustryDropdownsService,
        private readonly logService: LogService,
        private store$: Store<AppState>,
        private readonly vehicleService: VehicleService
    ) {}
}

export const vehicleEffectsModule = EffectsModule.forFeature([VehicleEffects]);
